import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowRightLeft, Building2, Copy, Globe2, Loader2, X } from 'lucide-react'
import api from '../../services/api'
import { useOpCo } from '../../context/OpCoContext'
import { workspaceNav, workspacePath } from '../../config/opcoWorkspace'

// Template OpCo UI (OpCo 10). A template's opco_id is its owning OpCo, or
// null = Unassigned (All OpCos view only). The backend only sends opco_id with the OpCo
// workspace on, so every export here renders nothing when it is off.

const CHIP = 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap flex-shrink-0'
const SELECT = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100'

export const SHARED_LABEL = 'Unassigned (All OpCos view)'
export const MOVE_CONFIRM_CODE = 'TEMPLATE_MOVE_NEEDS_CONFIRMATION'

function opcoById(opcos, id) {
  if (id === null || id === undefined) return null
  return (opcos || []).find((o) => String(o.opco_id) === String(id)) || null
}

function opcoByCode(opcos, code) {
  if (!code) return null
  return (opcos || []).find((o) => o.code.toLowerCase() === String(code).toLowerCase()) || null
}

function opcoLabel(o) {
  return o.name && o.name !== o.code ? `${o.code} - ${o.name}` : o.code
}

// True when a copy into `code` ('' = shared) stays in the source template's
// OpCo, the only case where it may reuse the source's analyzer.
export function sameTemplateOpco(sourceTemplate, code, opcos) {
  const source = sourceTemplate?.opco_id ?? null
  const target = code ? (opcoByCode(opcos, code)?.opco_id ?? code) : null
  return String(source) === String(target)
}

// "Shared" or the owning OpCo's code, on template cards, rows and the header.
export function TemplateOpCoChip({ template }) {
  const { enabled, opcos } = useOpCo()
  if (!enabled || !template || !('opco_id' in template)) return null
  if (template.opco_id === null || template.opco_id === undefined) {
    return (
      <span
        className={`${CHIP} bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300`}
        title="Unassigned: shown only in the All OpCos view; only Super Admins can assign it to an OpCo"
      >
        <Globe2 className="h-2.5 w-2.5" />
        Unassigned
      </span>
    )
  }
  const opco = opcoById(opcos, template.opco_id)
  return (
    <span
      className={`${CHIP} bg-navy-100 text-navy-700 dark:bg-navy-900/30 dark:text-navy-300`}
      title={opco?.name && opco.name !== opco.code ? opco.name : undefined}
    >
      <Building2 className="h-2.5 w-2.5" />
      {opco?.code || `OpCo ${template.opco_id}`}
    </span>
  )
}

// OpCo admins in the All OpCos view choose where a new template belongs.
export function useTemplateOpCoPicker() {
  const { enabled, isOpcoAdmin, allOpcos } = useOpCo()
  return Boolean(enabled && isOpcoAdmin && allOpcos)
}

export function TemplateOpCoPicker({ value, onChange, hint }) {
  const show = useTemplateOpCoPicker()
  const { opcos } = useOpCo()
  if (!show) return null
  return (
    <div>
      <label className="block text-[14px] font-medium text-gray-700 dark:text-gray-300 mb-1.5" htmlFor="template-opco-picker">
        OpCo
      </label>
      <select id="template-opco-picker" value={value} onChange={(e) => onChange(e.target.value)} className={SELECT}>
        <option value="">{SHARED_LABEL}</option>
        {(opcos || []).map((o) => (
          <option key={o.code} value={o.code}>{opcoLabel(o)}</option>
        ))}
      </select>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {hint || 'Unassigned templates show only in the All OpCos view until a Super Admin assigns them. An OpCo template is visible only in that OpCo.'}
      </p>
    </div>
  )
}

function DialogShell({ title, titleId, onClose, onSubmit, children, footer }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-navy-900/60 backdrop-blur-sm" onClick={onClose} />
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={onSubmit}
        className="relative w-full max-w-lg rounded-xl bg-white dark:bg-gray-900 shadow-xl ring-1 ring-gray-200 dark:ring-gray-700"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <h2 id={titleId} className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4 max-h-[65vh] overflow-y-auto">{children}</div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-800">{footer}</div>
      </form>
    </div>
  )
}

function ErrorLine({ text }) {
  if (!text) return null
  return (
    <p role="alert" className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
      {text}
    </p>
  )
}

const BTN_SECONDARY = 'px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
const BTN_PRIMARY = 'inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white bg-navy-600 hover:bg-navy-700 disabled:opacity-50 disabled:cursor-not-allowed'

// The 409 answer of move-opco: who loses the template, and what may break.
function MoveConfirmation({ details }) {
  const cutOff = details?.cut_off_opcos || []
  const warnings = details?.warnings || []
  return (
    <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-3 text-sm text-amber-900 dark:text-amber-200 space-y-2" data-testid="move-template-warnings">
      <p className="flex items-start gap-2 font-medium">
        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
        {details?.error || 'Check the following before moving this template.'}
      </p>
      {cutOff.length > 0 && (
        <ul className="list-disc pl-6 space-y-0.5">
          {cutOff.map((r) => (
            <li key={`${r.opco_id}`}>
              {r.opco_code || `OpCo ${r.opco_id}`}: {r.count} request{r.count === 1 ? '' : 's'} on this template. They keep it, but that OpCo can no longer use it for new work.
            </li>
          ))}
        </ul>
      )}
      {warnings.length > 0 && (
        <ul className="list-disc pl-6 space-y-0.5">
          {warnings.map((w) => (
            <li key={w.code}>
              {w.message}
              {Array.isArray(w.connectors) && w.connectors.length > 0 && (
                <span className="block text-xs opacity-80">
                  {w.connectors.map((c) => `${c.name || c.id} (${c.opco_code || `OpCo ${c.opco_id}`})`).join(', ')}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// After a change: the All OpCos view still shows the template, so refresh; in
// a single workspace follow it to its OpCo, or stay when it is now shared or
// still in this workspace.
function useFollowTemplate() {
  const { allOpcos, workspaceCode } = useOpCo()
  const navigate = useNavigate()
  return (templateId, code) => {
    const here = !code || allOpcos || (workspaceCode || '').toLowerCase() === code.toLowerCase()
    if (here) navigate(`/templates/${templateId}`)
    else workspaceNav.assign(workspacePath(code, `/templates/${templateId}`))
  }
}

// POST /api/templates/<id>/move-opco {opco_code | null, reason?, force?}.
export function ChangeTemplateOpCoDialog({ template, open, onClose, onChanged }) {
  const { opcos } = useOpCo()
  const [code, setCode] = useState(null) // null = nothing chosen yet, '' = shared
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [confirm, setConfirm] = useState(null)

  if (!open || !template) return null

  const current = opcoById(opcos, template.opco_id)
  const isShared = template.opco_id === null || template.opco_id === undefined
  const choices = (opcos || []).filter((o) => !current || o.code !== current.code)

  const reset = () => {
    setCode(null)
    setReason('')
    setError('')
    setConfirm(null)
  }
  const close = () => {
    if (saving) return
    reset()
    onClose?.()
  }

  const submit = async (e) => {
    e?.preventDefault()
    if (code === null || saving) return
    setSaving(true)
    setError('')
    try {
      const body = { opco_code: code || null }
      if (reason.trim()) body.reason = reason.trim()
      if (confirm) body.force = true
      const res = await api.post(`/templates/${template.id}/move-opco`, body)
      setSaving(false)
      reset()
      onChanged?.(res?.data, code || null)
      onClose?.()
    } catch (err) {
      setSaving(false)
      const data = err?.response?.data
      if (err?.response?.status === 409 && data?.code === MOVE_CONFIRM_CODE) {
        setConfirm(data)
        return
      }
      setError(data?.error || 'Could not change the OpCo. Please try again.')
    }
  }

  const choose = (value) => {
    setCode(value === '__none__' ? null : value)
    setConfirm(null)
    setError('')
  }

  return (
    <DialogShell
      title="Change OpCo"
      titleId="change-template-opco-title"
      onClose={close}
      onSubmit={submit}
      footer={(
        <>
          <button type="button" onClick={close} className={BTN_SECONDARY}>Cancel</button>
          <button type="submit" disabled={code === null || saving} className={BTN_PRIMARY}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirm ? 'Move anyway' : 'Change OpCo'}
          </button>
        </>
      )}
    >
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Now: <span className="font-medium">{isShared ? SHARED_LABEL : (current ? opcoLabel(current) : `OpCo ${template.opco_id}`)}</span>
      </p>
      <label className="block">
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Move to</span>
        <select value={code === null ? '__none__' : code} onChange={(e) => choose(e.target.value)} className={SELECT}>
          <option value="__none__">Select...</option>
          {!isShared && <option value="">{SHARED_LABEL}</option>}
          {choices.map((o) => (
            <option key={o.code} value={o.code}>{opcoLabel(o)}</option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason (optional)</span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          maxLength={500}
          className={SELECT}
        />
      </label>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Requests keep their OpCo. Access lists, approver, connectors and ERP settings are not changed.
      </p>
      {confirm && <MoveConfirmation details={confirm} />}
      <ErrorLine text={error} />
    </DialogShell>
  )
}

// A copy in another OpCo: same fields, its own analyzer (built later), no
// approver / access / connectors / ERP settings (set them for that OpCo).
export function CopyTemplateToOpCoDialog({ template, open, onClose, onCopied }) {
  const { opcos } = useOpCo()
  const [code, setCode] = useState(null)
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  if (!open || !template) return null

  const effectiveName = name || `${template.name} (copy)`
  const close = () => {
    if (saving) return
    setCode(null)
    setName('')
    setError('')
    onClose?.()
  }

  const submit = async (e) => {
    e?.preventDefault()
    if (code === null || !effectiveName.trim() || saving) return
    setSaving(true)
    setError('')
    try {
      const res = await api.post('/templates', {
        name: effectiveName.trim(),
        description: template.description || null,
        allow_reprocessing: template.allow_reprocessing !== false,
        source_template_id: template.id,
        reuse_source_analyzer: false,
        opco_code: code || null,
      })
      setSaving(false)
      const created = res?.data
      setCode(null)
      setName('')
      onCopied?.(created, code || null)
      onClose?.()
    } catch (err) {
      setSaving(false)
      setError(err?.response?.data?.error || 'Could not copy the template. Please try again.')
    }
  }

  return (
    <DialogShell
      title="Copy to OpCo"
      titleId="copy-template-opco-title"
      onClose={close}
      onSubmit={submit}
      footer={(
        <>
          <button type="button" onClick={close} className={BTN_SECONDARY}>Cancel</button>
          <button type="submit" disabled={code === null || !effectiveName.trim() || saving} className={BTN_PRIMARY}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Copy
          </button>
        </>
      )}
    >
      <label className="block">
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</span>
        <input value={effectiveName} onChange={(e) => setName(e.target.value)} maxLength={255} className={SELECT} />
      </label>
      <label className="block">
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Copy to</span>
        <select value={code === null ? '__none__' : code} onChange={(e) => setCode(e.target.value === '__none__' ? null : e.target.value)} className={SELECT}>
          <option value="__none__">Select...</option>
          <option value="">{SHARED_LABEL}</option>
          {(opcos || []).map((o) => (
            <option key={o.code} value={o.code}>{opcoLabel(o)}</option>
          ))}
        </select>
      </label>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        The fields are copied. The copy builds its own analyzer, and its approver, access, connectors and
        NetSuite / Boomi settings start empty so you can set them for that OpCo.
      </p>
      <ErrorLine text={error} />
    </DialogShell>
  )
}

// Header buttons on the template page, OpCo admins only.
export function TemplateOpCoActions({ template, onChanged }) {
  const { enabled, isOpcoAdmin, allOpcos } = useOpCo()
  const [open, setOpen] = useState(null) // 'move' | 'copy'
  const follow = useFollowTemplate()
  if (!enabled || !isOpcoAdmin || !template || !('opco_id' in template)) return null
  const btn = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold text-navy-600 dark:text-navy-300 hover:bg-navy-50 dark:hover:bg-navy-900/20 whitespace-nowrap'
  return (
    <>
      <button type="button" onClick={() => setOpen('move')} className={btn}>
        <ArrowRightLeft className="h-3 w-3" />
        Change OpCo
      </button>
      <button type="button" onClick={() => setOpen('copy')} className={btn}>
        <Copy className="h-3 w-3" />
        Copy to OpCo
      </button>
      <ChangeTemplateOpCoDialog
        template={template}
        open={open === 'move'}
        onClose={() => setOpen(null)}
        onChanged={(data, code) => {
          if (allOpcos || !code) onChanged?.(data)
          else follow(template.id, code)
        }}
      />
      <CopyTemplateToOpCoDialog
        template={template}
        open={open === 'copy'}
        onClose={() => setOpen(null)}
        onCopied={(created, code) => { if (created?.id) follow(created.id, code) }}
      />
    </>
  )
}
